import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { useCanEdit } from "@/lib/project-access";
import {
  getPrimaryVerdict,
  type VerdictRating,
} from "@/components/experience/vocabulary";
import { runTime } from "@/lib/run-trend";
import { isUnresolved } from "@/components/finding-badges";
import { useWorkRequests } from "@/components/experience/use-work-requests";

import type { PersonaPanelProps, WalkthroughRow } from "./panel-types";
import { PanelHeader } from "./panel-header";
import { StateLine } from "./panel-state-line";
import { BriefBlock } from "./panel-brief";
import { WalkthroughsBlock } from "./panel-walkthroughs";
import { FindingsBlock } from "./panel-findings";

export function PersonaPanel({
  projectId,
  outlineId,
  personaId,
  scenarioId: scenarioIdProp,
  defaultExpanded = false,
}: PersonaPanelProps): JSX.Element {
  // Resolve the scenario binding for (outlineId, personaId). Skip the
  // network round-trip when the parent already knows the binding id —
  // critical for the unified scenario detail page which renders one
  // panel per cast persona and would otherwise issue N lookup queries.
  const lookupEnabled =
    !scenarioIdProp && !!projectId && !!outlineId && !!personaId;
  const { data: scenarios } = trpc.scenario.list.useQuery(
    { projectId, outlineId, personaId },
    { enabled: lookupEnabled },
  );
  const scenarioId =
    scenarioIdProp ??
    (scenarios && scenarios.length > 0 ? scenarios[0].id : null);

  const requests = useWorkRequests(projectId);

  const { data: scenario } = trpc.scenario.getById.useQuery(
    { id: scenarioId ?? "" },
    { enabled: !!scenarioId },
  );

  const { data: walkthroughsList } = trpc.walkthrough.list.useQuery(
    { scenarioId: scenarioId ?? "" },
    {
      enabled: !!scenarioId,
      refetchInterval: (query) => {
        const data = query.state.data;
        if (!data) return false;
        const hasActive = data.some(
          (wt) => wt.status === "running" || wt.status === "pending",
        );
        return hasActive ? 3000 : false;
      },
    },
  );

  const walkthroughs = (walkthroughsList ?? []) as WalkthroughRow[];
  const completedWalkthroughs = walkthroughs.filter(
    (wt) => wt.status === "completed",
  );
  const hasCompletedWalkthroughs = completedWalkthroughs.length > 0;

  const { data: lifecycle } = trpc.finding.lifecycleByScenario.useQuery(
    { projectId, scenarioId: scenarioId ?? "" },
    { enabled: !!projectId && !!scenarioId && hasCompletedWalkthroughs },
  );

  const canonicalFindings = (lifecycle?.canonicalFindings ?? []) as Array<{
    id: string;
    title: string | null;
    description: string | null;
    severity: string | null;
    type: string | null;
    taskImpact: string | null;
    recommendation: string | null;
    representativeFindingId: string | null;
    status?: string | null;
    triageStatus?: string | null;
    state?: string | null;
    stateReason?: string | null;
  }>;

  // ── State-line derivation (matches PersonaBindingChip vocabulary) ─────
  const runCount = completedWalkthroughs.length;
  // Quality counts move only on re-run evidence: a fix applied but not yet
  // re-run still counts as open (CONTRACT decision 1).
  const lifecycleOf = (f: (typeof canonicalFindings)[number]) =>
    f.state ?? f.status ?? f.triageStatus ?? "open";
  const openCount = canonicalFindings.filter((f) =>
    isUnresolved(lifecycleOf(f)),
  ).length;
  const criticalCount = canonicalFindings.filter(
    (f) => f.severity === "critical" && isUnresolved(lifecycleOf(f)),
  ).length;
  const latestCompleted = useMemo(() => {
    const sorted = [...completedWalkthroughs].sort(
      (a, b) => runTime(b) - runTime(a),
    );
    return sorted[0] ?? null;
  }, [completedWalkthroughs]);
  const latestRating: VerdictRating | null = latestCompleted
    ? getPrimaryVerdict(
        latestCompleted.verdict as Array<{ rating: VerdictRating }> | null,
      )
    : null;

  // ── Expansion state ──────────────────────────────────────────────────
  const [open, setOpen] = useState(defaultExpanded);
  useEffect(() => {
    if (defaultExpanded) {
      setOpen(true);
      return;
    }
    if (
      typeof window !== "undefined" &&
      window.location.hash === `#persona-${personaId}`
    ) {
      setOpen(true);
      const el = document.getElementById(`persona-${personaId}`);
      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({ block: "start" });
        });
      }
    }
  }, [defaultExpanded, personaId]);

  const s = scenario ?? null;
  const persona = s?.persona ?? null;
  const personaName = persona?.name ?? "This persona";
  const firstName = personaName.split(" ")[0];

  const canEdit = useCanEdit();

  return (
    <section
      id={`persona-${personaId}`}
      aria-label={`Persona: ${personaName}`}
      className="scroll-mt-16"
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="p-5 sm:p-6">
          <PanelHeader
            persona={persona}
            stateLine={
              <StateLine
                scenarioId={scenarioId}
                runCount={runCount}
                openCount={openCount}
                criticalCount={criticalCount}
                latestRating={latestRating}
              />
            }
            scenarioRequest={
              scenarioId ? (requests.byScenario.get(scenarioId) ?? null) : null
            }
            onCancelRequest={
              canEdit && scenarioId && requests.byScenario.has(scenarioId)
                ? () => requests.cancel(requests.byScenario.get(scenarioId)!.id)
                : undefined
            }
            personaName={personaName}
            scenarioId={scenarioId}
            projectId={projectId}
            open={open}
          />

          <CollapsibleContent className="mt-6 space-y-8">
            {persona && scenarioId && (
              <BriefBlock
                scenarioId={scenarioId}
                brief={s?.brief ?? null}
                personaName={personaName}
                firstName={firstName}
              />
            )}

            {walkthroughs.length > 0 && (
              <WalkthroughsBlock
                walkthroughs={walkthroughs}
                scenarioName={s?.outline?.name ?? "Scenario"}
                projectId={projectId}
              />
            )}

            <FindingsBlock
              projectId={projectId}
              canonicalFindings={canonicalFindings}
              hasCompletedWalkthroughs={hasCompletedWalkthroughs}
              firstName={firstName}
            />
          </CollapsibleContent>
        </div>
      </Collapsible>
    </section>
  );
}
